import { useEffect, useRef, type RefObject } from "react";
import { pushHisDebugLog } from "../utils/hisDebugLog";
import {
  setLayoutDebugData,
  sliceElement,
  type LayoutDebugData,
  type StoreRevenueMonitorLayoutSlices,
} from "../utils/layoutDebugSnapshot";

function readMemory():
  | { usedJSHeapSize?: number; totalJSHeapSize?: number; jsHeapSizeLimit?: number }
  | undefined {
  const perf = globalThis.performance as Performance & {
    memory?: {
      usedJSHeapSize: number;
      totalJSHeapSize: number;
      jsHeapSizeLimit: number;
    };
  };
  if (!perf?.memory) return undefined;
  return {
    usedJSHeapSize: perf.memory.usedJSHeapSize,
    totalJSHeapSize: perf.memory.totalJSHeapSize,
    jsHeapSizeLimit: perf.memory.jsHeapSizeLimit,
  };
}

export type StoreRevenueMonitorLayoutMeasureRefs = {
  containerRef: RefObject<HTMLElement | null>;
  leftInnerRef: RefObject<HTMLElement | null>;
  statCardsRef: RefObject<HTMLElement | null>;
  searchWrapRef: RefObject<HTMLElement | null>;
  listHostRef: RefObject<HTMLElement | null>;
  listScrollRef: RefObject<HTMLElement | null>;
  aiWrapRef: RefObject<HTMLElement | null>;
};

function pickPanel(el: HTMLElement | null | undefined): HTMLElement | null {
  if (!el) return null;
  const panel = el.closest(".ant-splitter-panel");
  return panel instanceof HTMLElement ? panel : null;
}

/**
 * 门店营收监控：周期性写入 layoutDebugSnapshot，供 DBG 浮窗订阅；
 * 同时在 scrollHeight 变化明显时写一条 hisDebugLog（与单店页 hook 行为对齐）。
 */
export function useStoreRevenueMonitorLayoutDebugSampling(
  enabled: boolean,
  refs: StoreRevenueMonitorLayoutMeasureRefs,
): void {
  const prevListScrollHRef = useRef<number | null>(null);
  const prevCompactRef = useRef<string>("");

  useEffect(() => {
    if (!enabled || typeof window === "undefined") {
      setLayoutDebugData(null);
      return undefined;
    }

    const tick = (): void => {
      const path = globalThis.location?.pathname ?? "";
      const rootEl = document.getElementById("root");
      const body = document.body;
      const appShell =
        rootEl?.firstElementChild instanceof HTMLElement
          ? rootEl.firstElementChild
          : null;

      const c = refs.containerRef.current;
      /* Splitter 在监控容器内部，不能用 closest（会往祖先找而找不到） */
      const splitterNode = c?.querySelector(".ant-splitter");
      const splitter =
        splitterNode instanceof HTMLElement ? splitterNode : null;
      const leftInner = refs.leftInnerRef.current;
      const leftPanel = pickPanel(leftInner);
      const aiWrap = refs.aiWrapRef.current;
      const rightPanel = pickPanel(aiWrap);

      const listScroll = refs.listScrollRef.current;

      const revenueMonitor: StoreRevenueMonitorLayoutSlices = {
        root: rootEl ? sliceElement(rootEl, "root") : undefined,
        body: body ? sliceElement(body, "body") : undefined,
        appShell: appShell ? sliceElement(appShell, "app-shell") : undefined,
        monitorContainer: c ? sliceElement(c, "monitor-container") : undefined,
        splitter: splitter ? sliceElement(splitter, "splitter") : undefined,
        leftPanel: leftPanel ? sliceElement(leftPanel, "left-panel") : undefined,
        leftInner: leftInner ? sliceElement(leftInner, "left-inner") : undefined,
        statCards: refs.statCardsRef.current
          ? sliceElement(refs.statCardsRef.current, "stat-cards")
          : undefined,
        searchWrap: refs.searchWrapRef.current
          ? sliceElement(refs.searchWrapRef.current, "search-wrap")
          : undefined,
        listHost: refs.listHostRef.current
          ? sliceElement(refs.listHostRef.current, "list-host")
          : undefined,
        listScroll: listScroll ? sliceElement(listScroll, "list-scroll") : undefined,
        rightPanel: rightPanel ? sliceElement(rightPanel, "right-panel") : undefined,
        aiWrap: aiWrap ? sliceElement(aiWrap, "ai-wrap") : undefined,
      };

      const data: LayoutDebugData = {
        ts: new Date().toISOString(),
        path,
        windowInner: {
          width: window.innerWidth,
          height: window.innerHeight,
          dpr: window.devicePixelRatio || 1,
        },
        documentElement: {
          clientWidth: document.documentElement.clientWidth,
          clientHeight: document.documentElement.clientHeight,
        },
        memory: readMemory(),
        revenueMonitor,
      };
      setLayoutDebugData(data);

      const line = [
        "layout:DBG",
        `root=${revenueMonitor.root?.clientHeight ?? "—"}`,
        `app=${revenueMonitor.appShell?.clientHeight ?? "—"}`,
        `mon=${revenueMonitor.monitorContainer?.clientHeight ?? "—"}`,
        `split=${revenueMonitor.splitter?.clientHeight ?? "—"}`,
        `L=${revenueMonitor.leftPanel?.clientHeight ?? "—"}`,
        `stat=${revenueMonitor.statCards?.clientHeight ?? "—"}`,
        `search=${revenueMonitor.searchWrap?.clientHeight ?? "—"}`,
        `listH=${revenueMonitor.listHost?.clientHeight ?? "—"}`,
        `list=${revenueMonitor.listScroll?.clientHeight ?? "—"}/${revenueMonitor.listScroll?.scrollHeight ?? "—"}`,
        `R=${revenueMonitor.rightPanel?.clientHeight ?? "—"}`,
        `ai=${revenueMonitor.aiWrap?.clientHeight ?? "—"}`,
      ].join(" ");
      const prevLine = prevCompactRef.current;
      if (line !== prevLine) {
        prevCompactRef.current = line;
        pushHisDebugLog(line);
      }

      if (listScroll) {
        const sh = listScroll.scrollHeight;
        const prev = prevListScrollHRef.current;
        if (prev != null && Math.abs(sh - prev) >= 12) {
          pushHisDebugLog(
            `layout:monitor listScroll scrollHeight ${prev}→${sh} (clientH=${listScroll.clientHeight})`,
          );
        }
        prevListScrollHRef.current = sh;
      } else {
        prevListScrollHRef.current = null;
      }
    };

    tick();
    const intervalId = window.setInterval(tick, 1200);
    const onResize = () => tick();
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        pushHisDebugLog(
          `visibility:visible (hidden was ${document.hidden})`,
        );
      }
      tick();
    };
    window.addEventListener("resize", onResize);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", onVisibility);
      setLayoutDebugData(null);
    };
  }, [enabled, refs]);
}
